/**
 * Mentions « @Prénom Nom » dans la messagerie (retour #38). Une mention est du
 * texte ordinaire : « @ » suivi du nom affiché d'un membre, rien de plus n'est
 * stocké ni envoyé au serveur. La personne citée est prévenue par son navigateur
 * (messaging/store.ts → GameHUD), le nom est mis en valeur à l'affichage.
 */

const WORD = /[\p{L}\p{N}]/u;

/** Minuscules sans accents, pour comparer ce qu'on tape aux noms. */
export function foldName(s: string): string {
  return s.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

/** « @nom » au début du texte ou après un caractère qui n'est pas une lettre, et pas collé à la suite d'un mot. */
function mentionAt(text: string, index: number, name: string): boolean {
  if (text[index] !== '@') return false;
  if (index > 0 && WORD.test(text[index - 1]!)) return false;
  const end = index + 1 + name.length;
  if (foldName(text.slice(index + 1, end)) !== foldName(name)) return false;
  return end >= text.length || !WORD.test(text[end]!);
}

/** Le texte mentionne-t-il cette personne ? */
export function mentionsName(text: string, name: string): boolean {
  if (!name.trim()) return false;
  for (let i = text.indexOf('@'); i !== -1; i = text.indexOf('@', i + 1)) {
    if (mentionAt(text, i, name)) return true;
  }
  return false;
}

export type MentionPart = { text: string; mention?: { userId: string } };

/** Découpe le texte en morceaux, les mentions de membres connus à part (nom le plus long d'abord). */
export function splitMentions(text: string, members: Array<{ userId: string; displayName: string }>): MentionPart[] {
  if (!text.includes('@') || members.length === 0) return [{ text }];
  const sorted = members.filter((m) => m.displayName.trim()).sort((a, b) => b.displayName.length - a.displayName.length);
  const parts: MentionPart[] = [];
  let last = 0;
  for (let i = text.indexOf('@'); i !== -1; i = text.indexOf('@', i + 1)) {
    const hit = sorted.find((m) => mentionAt(text, i, m.displayName));
    if (!hit) continue;
    if (i > last) parts.push({ text: text.slice(last, i) });
    const end = i + 1 + hit.displayName.length;
    parts.push({ text: text.slice(i, end), mention: { userId: hit.userId } });
    last = end;
    i = end - 1;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

/**
 * Mention en cours de frappe juste avant le curseur : « @ » en début de texte
 * ou après une espace, puis au plus 30 caractères sans retour à la ligne.
 * Renvoie la position du « @ » et ce qui suit, ou null.
 */
export function mentionQueryAt(value: string, caret: number): { start: number; query: string } | null {
  const before = value.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at === -1 || caret - at > 31) return null;
  if (at > 0 && !/\s/.test(before[at - 1]!)) return null;
  const query = before.slice(at + 1);
  // Espace en dernier : la mention est finie (Entrée envoie le message), elle reprend si on continue le nom.
  if (/[\n@]/.test(query) || /^\s/.test(query) || /\s$/.test(query)) return null;
  return { start: at, query };
}

/** Membres dont le nom commence par la saisie (nom complet ou un de ses mots). */
export function matchMembers<T extends { displayName: string }>(members: T[], query: string, limit = 6): T[] {
  const q = foldName(query.trim());
  const scored = members
    .map((m) => {
      const n = foldName(m.displayName);
      const rank = n.startsWith(q) ? 0 : n.split(/\s+/).some((w) => w.startsWith(q)) ? 1 : -1;
      return { m, rank };
    })
    .filter((x) => x.rank >= 0)
    .sort((a, b) => a.rank - b.rank || a.m.displayName.localeCompare(b.m.displayName, 'fr'));
  return scored.slice(0, limit).map((x) => x.m);
}
